import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Paper,
  Box,
  Chip,
  CircularProgress,
  Divider,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [internalNotes, setInternalNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const fetchTicketDetail = async () => {
    try {
      const response = await api.get(`/tickets/${id}`);
      if (response.data.success) {
        console.log("Ticket fetched:", {
          id: response.data.ticket._id,
          status: response.data.ticket.status,
          title: response.data.ticket.title,
        });
        setTicket(response.data.ticket);
        setComments(response.data.comments || []);
        setInternalNotes(response.data.ticket.internalNotes || "");
      }
    } catch (error) {
      console.error("Failed to fetch ticket:", error);
      setError("Failed to load ticket details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      setError("");
      console.log("=== Adding Comment ===");
      console.log("User info:", {
        email: user?.email,
        role: user?.role,
        displayName: user?.displayName,
      });
      console.log("Ticket ID:", id);
      console.log("Current ticket status:", ticket?.status);

      const response = await api.post(`/tickets/${id}/comments`, {
        content: newComment,
      });

      console.log("=== Server Response ===");
      console.log("Success:", response.data.success);
      console.log("Ticket Status:", response.data.ticketStatus);
      console.log("Status Changed:", response.data.statusChanged);
      console.log("Debug Info:", response.data.debugInfo);

      if (response.data.success) {
        console.log("Comment added successfully");
        setComments([...comments, response.data.comment]);
        setNewComment("");
        // Refresh ticket details to get updated status
        console.log("Fetching updated ticket details...");
        await fetchTicketDetail();
      }
    } catch (error) {
      console.error("Failed to add comment:", error);
      setError("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseTicket = async () => {
    try {
      setClosing(true);
      setError("");
      setSuccess("");

      const response = await api.put(`/tickets/${id}/status`, {
        status: "closed",
      });

      if (response.data.success) {
        setTicket(response.data.ticket);
        setSuccess(t("ticketDetail.closeSuccess"));
        setCloseDialogOpen(false);
        // Refresh ticket details to get the system comment
        await fetchTicketDetail();
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (error) {
      console.error("Failed to close ticket:", error);
      setError(error.response?.data?.message || t("ticketDetail.closeFailed"));
      setCloseDialogOpen(false);
    } finally {
      setClosing(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      open: "open",
      "in-progress": "inProgress",
      "waiting-reply": "waitingReply",
      resolved: "resolved",
      closed: "closed",
    };
    return statusMap[status] || status;
  };

  const getCategoryLabel = (category) => {
    const categoryMap = {
      technical: "technical",
      billing: "billing",
      general: "general",
      "feature-request": "featureRequest",
      other: "other",
    };
    return categoryMap[category] || category;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: "warning",
      "in-progress": "info",
      resolved: "success",
      closed: "default",
    };
    return colors[status] || "default";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "default",
      medium: "info",
      high: "warning",
      urgent: "error",
    };
    return colors[priority] || "default";
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      setError("");
      const response = await api.put(`/tickets/${id}/notes`, {
        internalNotes,
      });
      if (response.data.success) {
        setSuccess(
          t("ticketDetail.notesSaved") || "Internal notes saved successfully"
        );
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (error) {
      setError(
        t("ticketDetail.notesSaveFailed") || "Failed to save internal notes"
      );
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box>
        <Alert severity="error">{t("ticketDetail.notFound")}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard")}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <div>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/dashboard")}
        sx={{ mb: 2 }}
      >
        {t("ticketDetail.backToDashboard")}
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {t("ticket.number")} #{ticket.ticketNumber || "N/A"}:{" "}
              {ticket.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {t("ticketDetail.ticketId")}: {ticket._id}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label={t(`status.${getStatusLabel(ticket.status)}`)}
              color={getStatusColor(ticket.status)}
            />
            <Chip
              label={t(`priority.${ticket.priority}`)}
              color={getPriorityColor(ticket.priority)}
            />
            <Chip label={t(`category.${getCategoryLabel(ticket.category)}`)} />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("ticketDetail.createdBy")}: {ticket.createdBy?.displayName} (
            {ticket.createdBy?.metamaskAddress ||
              ticket.createdBy?.email ||
              "Unknown"}
            )
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("ticketDetail.createdOn")}:{" "}
            {new Date(ticket.createdAt).toLocaleString()}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="body1"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {ticket.description}
        </Typography>

        {/* Close Ticket Button - Show for support/admin or ticket creator, if ticket is not closed */}
        {(user?.role === "support" ||
          user?.role === "admin" ||
          ticket.createdBy?._id === user?.id) &&
          ticket.status !== "closed" && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CloseIcon />}
                onClick={() => setCloseDialogOpen(true)}
                disabled={closing}
              >
                {t("ticketDetail.closeTicket")}
              </Button>
            </Box>
          )}
      </Paper>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        {t("ticketDetail.comments")} ({comments.length})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        {comments.map((comment) => {
          // Check if this is a system message
          const isSystemMessage = comment.isSystemMessage || false;

          // Use supportSettings displayName if available for support/admin users
          console.log("Comment author data:", {
            role: comment.author?.role,
            displayName: comment.author?.displayName,
            supportSettings: comment.author?.supportSettings,
            isSystemMessage: isSystemMessage,
          });

          // Determine display name based on role and settings
          let displayName;
          if (isSystemMessage) {
            displayName = t("ticketDetail.system");
          } else if (
            (comment.author?.role === "support" ||
              comment.author?.role === "admin") &&
            comment.author?.supportSettings?.displayName
          ) {
            // Use support display name for support/admin users
            displayName = comment.author.supportSettings.displayName;
          } else {
            // Use regular display name for all other users
            displayName = comment.author?.displayName || "Unknown User";
          }

          console.log("Using displayName:", displayName);

          return (
            <Card
              key={comment._id}
              sx={{
                mb: 2,
                backgroundColor: isSystemMessage
                  ? "info.lighter"
                  : "background.paper",
                border: isSystemMessage ? "1px solid" : "none",
                borderColor: isSystemMessage ? "info.main" : "transparent",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      mr: 1,
                      bgcolor: isSystemMessage ? "info.main" : "primary.main",
                    }}
                  >
                    {isSystemMessage
                      ? "🤖"
                      : displayName?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2">
                      {displayName}
                      {isSystemMessage && (
                        <Chip
                          label={t("ticketDetail.systemTag")}
                          size="small"
                          color="info"
                          sx={{ ml: 1, height: 20 }}
                        />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(comment.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    ml: 5,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {comment.content}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Comment Form - Only show if ticket is not closed */}
      {ticket.status !== "closed" ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t("ticketDetail.addComment")}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={t("ticketDetail.commentPlaceholder")}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={submitting}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={handleAddComment}
            disabled={!newComment.trim() || submitting}
          >
            {submitting
              ? t("ticketDetail.posting")
              : t("ticketDetail.postComment")}
          </Button>
        </Paper>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("ticketDetail.closedMessage")}
        </Alert>
      )}

      {/* Internal Notes - Only for support/admin */}
      {(user?.role === "support" || user?.role === "admin") && (
        <Paper sx={{ p: 3, mt: 3, backgroundColor: "#fff3e0" }}>
          <Typography variant="h6" gutterBottom color="warning.main">
            {t("ticketDetail.internalNotes")}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 2 }}>
            {t("ticketDetail.internalNotesDesc")}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder={t("ticketDetail.internalNotesPlaceholder")}
            variant="outlined"
            inputProps={{ maxLength: 2000 }}
            helperText={`${internalNotes.length}/2000`}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleSaveNotes}
            disabled={savingNotes}
            color="warning"
          >
            {savingNotes
              ? t("ticketDetail.saving")
              : t("ticketDetail.saveNotes")}
          </Button>
        </Paper>
      )}

      {/* Close Ticket Confirmation Dialog */}
      <Dialog
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
        aria-labelledby="close-dialog-title"
        aria-describedby="close-dialog-description"
      >
        <DialogTitle id="close-dialog-title">
          {t("closeDialog.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="close-dialog-description">
            {t("closeDialog.message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)} disabled={closing}>
            {t("closeDialog.cancel")}
          </Button>
          <Button
            onClick={handleCloseTicket}
            color="error"
            variant="contained"
            disabled={closing}
            autoFocus
          >
            {closing ? t("ticketDetail.posting") : t("closeDialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TicketDetail;
